import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Cartao from '@/components/ui/Cartao';
import Botao from '@/components/ui/Botao';
import Campo from '@/components/ui/Campo';
import type { ContaSenha, LinkSenha } from '../types';
import {
  formatarTelefone,
  horaMinuto,
  minutosRestantes,
  normalizarContasSenha,
  normalizarLinkSenha,
  telefoneParaWa,
} from '../utils';

const TEXTO_CUIDADO = 'Só mande para a própria pessoa. Ela vai confirmar CPF e data de nascimento.';

function soDigitos(v: string | null | undefined): string {
  return (v ?? '').replace(/\D/g, '');
}

function dataCurta(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}/${d.getFullYear()}`;
}

function rotuloCpf(conta: ContaSenha): string {
  if (!conta.tem_cpf || !conta.cpf_final) return 'Sem CPF no cadastro';
  // O banco já manda mascarado (ex.: ***858).
  return `CPF ${conta.cpf_final}`;
}

// ---------------------------------------------------------------------------
// Aba "Ajudar com senha" (só admin da plataforma).
// Busca a conta, gera um link de 10 minutos e deixa copiar/mandar no WhatsApp.
// O link é de uso único: gerar de novo cancela o anterior (o banco cuida).
// ---------------------------------------------------------------------------
export default function AjudarSenha() {
  const [termo, setTermo] = useState('');
  const [contas, setContas] = useState<ContaSenha[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [erroBusca, setErroBusca] = useState<string | null>(null);

  const [gerando, setGerando] = useState<string | null>(null);
  const [erroLink, setErroLink] = useState<string | null>(null);
  const [link, setLink] = useState<LinkSenha | null>(null);
  const [contaDoLink, setContaDoLink] = useState<ContaSenha | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());

  // Busca a partir de 3 letras, esperando 400 ms para não atropelar o banco.
  useEffect(() => {
    const limpo = termo.trim();
    if (limpo.length < 3) {
      setContas(null);
      setErroBusca(null);
      setBuscando(false);
      return;
    }

    let ativo = true;
    setBuscando(true);
    const t = window.setTimeout(() => {
      (async () => {
        try {
          const { data, error } = await supabase.rpc('senha_link_buscar', { p_busca: limpo });
          if (error) throw error;
          const d = (data ?? {}) as Record<string, unknown>;
          if (d.ok === false) {
            if (ativo) {
              setErroBusca(
                typeof d.erro === 'string' && d.erro
                  ? d.erro
                  : 'Não foi possível buscar agora.',
              );
              setContas([]);
            }
            return;
          }
          if (ativo) {
            setErroBusca(null);
            setContas(normalizarContasSenha(d.contas));
          }
        } catch (e) {
          if (ativo) {
            setErroBusca(mensagemAmigavel(e));
            setContas([]);
          }
        } finally {
          if (ativo) setBuscando(false);
        }
      })();
    }, 400);

    return () => {
      ativo = false;
      window.clearTimeout(t);
    };
  }, [termo]);

  // Relógio do contador do link.
  useEffect(() => {
    if (!link) return undefined;
    const t = window.setInterval(() => setAgora(Date.now()), 15000);
    return () => window.clearInterval(t);
  }, [link]);

  async function gerar(conta: ContaSenha) {
    setGerando(conta.usuario_id);
    setErroLink(null);
    setCopiado(false);
    try {
      const { data, error } = await supabase.rpc('senha_link_gerar', {
        p_usuario: conta.usuario_id,
      });
      if (error) throw error;
      const d = (data ?? {}) as Record<string, unknown>;
      const novo = normalizarLinkSenha(d);
      if (!novo) {
        setErroLink(
          typeof d.erro === 'string' && d.erro
            ? d.erro
            : 'Não deu para gerar o link agora. Tente de novo.',
        );
        setLink(null);
        setContaDoLink(null);
        return;
      }
      setAgora(Date.now());
      setLink(novo);
      setContaDoLink(conta);
    } catch (e) {
      setErroLink(mensagemAmigavel(e));
      setLink(null);
      setContaDoLink(null);
    } finally {
      setGerando(null);
    }
  }

  async function copiar() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.mensagem);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2500);
    } catch {
      setCopiado(false);
    }
  }

  const restantes = link ? minutosRestantes(link.expira_em, agora) : 0;
  const telefoneDoLink = contaDoLink?.telefone ? telefoneParaWa(contaDoLink.telefone) : '';
  const whatsappDoLink =
    link && telefoneDoLink
      ? `https://wa.me/${telefoneDoLink}?text=${encodeURIComponent(link.mensagem)}`
      : '';

  return (
    <div className="space-y-5">
      <Cartao className="p-4 md:p-5">
        <div className="space-y-3">
          <Campo
            rotulo="Nome, e-mail ou CPF"
            value={termo}
            onChange={setTermo}
            placeholder="Ex.: Fernando, fernando@..., 000.000.000-00"
            autoComplete="off"
          />
          <p className="text-sm leading-relaxed text-foreground-500">
            Digite pelo menos 3 letras. O e-mail aparece mascarado — ninguém vê o endereço inteiro.
          </p>
        </div>
      </Cartao>

      <div className="rounded-2xl border border-accent-300 bg-accent-100/80 px-4 py-3">
        <p className="flex items-start gap-2 text-sm leading-relaxed text-accent-900">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-shield-check-line" aria-hidden />
          </span>
          {TEXTO_CUIDADO}
        </p>
      </div>

      {erroLink && (
        <p className="text-sm text-danger-600" role="alert">
          {erroLink}
        </p>
      )}

      {link && (
        <Cartao className="p-4 md:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-foreground-950">Link de nova senha</p>
            <span className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-900">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-time-line" aria-hidden />
              </span>
              {link.expira_em
                ? `vale até ${horaMinuto(link.expira_em)} (${restantes} min)`
                : `vence em ${restantes} min`}
            </span>
          </div>

          <p className="mt-3 break-all rounded-xl border border-secondary-200 bg-background-100 px-3 py-2 text-sm text-foreground-800">
            {link.link}
          </p>

          {link.aviso && (
            <p className="mt-3 flex items-start gap-2 rounded-xl border border-warning-300 bg-warning-100 px-3 py-2 text-sm leading-relaxed text-warning-900">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                <i className="ri-alert-line" aria-hidden />
              </span>
              {link.aviso}
            </p>
          )}

          <p className="mt-3 text-xs leading-relaxed text-foreground-500">
            {link.pede_cpf && link.pede_nascimento
              ? 'Ela vai confirmar CPF e data de nascimento.'
              : link.pede_cpf
                ? 'Ela vai confirmar o CPF.'
                : link.pede_nascimento
                  ? 'Ela vai confirmar a data de nascimento.'
                  : 'Ela só precisa criar a senha nova.'}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {whatsappDoLink && (
              <Botao
                variante="primario"
                tamanho="lg"
                cheio
                icone="ri-whatsapp-line"
                onClick={() => window.open(whatsappDoLink, '_blank', 'noopener,noreferrer')}
              >
                Mandar no WhatsApp
              </Botao>
            )}
            <Botao
              variante="secundario"
              icone={copiado ? 'ri-check-line' : 'ri-file-copy-line'}
              onClick={() => void copiar()}
            >
              {copiado ? 'Copiado' : 'Copiar mensagem'}
            </Botao>
          </div>

          {!whatsappDoLink && (
            <p className="mt-3 text-[13px] leading-relaxed text-foreground-600">
              Esta conta não tem telefone. Copie a mensagem e mande no WhatsApp da pessoa.
            </p>
          )}

          <p className="mt-3 text-xs leading-relaxed text-foreground-500">
            Se gerar outro link, este deixa de valer.
          </p>
        </Cartao>
      )}

      {buscando && contas === null && (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-[18px] bg-background-200/70" />
          ))}
        </div>
      )}

      {erroBusca && (
        <p className="text-sm text-danger-600" role="alert">
          {erroBusca}
        </p>
      )}

      {!buscando && contas !== null && contas.length === 0 && !erroBusca && (
        <Cartao className="p-6 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-user-search-line text-xl" aria-hidden />
          </span>
          <p className="mt-3 text-base font-semibold text-foreground-950">Ninguém encontrado</p>
          <p className="mt-1 text-sm text-foreground-600">
            Confira a escrita do nome ou tente o CPF completo.
          </p>
        </Cartao>
      )}

      {!buscando && contas !== null && contas.length > 0 && (
        <div className="space-y-3">
          {contas.map((conta) => (
            <Cartao key={conta.usuario_id} className="p-4 md:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-foreground-950">
                    {conta.nome || 'Sem nome'}
                  </p>
                  <p className="mt-0.5 truncate text-sm text-foreground-600">
                    {conta.email || 'sem e-mail'}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
                      {rotuloCpf(conta)}
                    </span>
                    {conta.telefone && (
                      <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
                        {formatarTelefone(conta.telefone)}
                      </span>
                    )}
                    {conta.ultimo_acesso && (
                      <span className="text-xs text-foreground-500">
                        último acesso {dataCurta(conta.ultimo_acesso)}
                      </span>
                    )}
                  </div>
                </div>
                <Botao
                  variante={link && contaDoLink?.usuario_id === conta.usuario_id ? 'secundario' : 'primario'}
                  icone="ri-lock-unlock-line"
                  carregando={gerando === conta.usuario_id}
                  onClick={() => void gerar(conta)}
                >
                  {link && contaDoLink?.usuario_id === conta.usuario_id
                    ? 'Gerar outro link'
                    : 'Gerar link de nova senha'}
                </Botao>
              </div>
              {!conta.tem_cpf || !conta.tem_nascimento ? (
                <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
                  <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    <i className="ri-information-line" aria-hidden />
                  </span>
                  {!conta.tem_cpf
                    ? 'Esta conta não tem CPF no cadastro — o link vai pedir só a senha nova.'
                    : 'Esta conta não tem data de nascimento — o link vai pedir só o CPF.'}
                </p>
              ) : null}
            </Cartao>
          ))}
        </div>
      )}

      {soDigitos(termo).length > 0 && soDigitos(termo).length < 3 && termo.trim().length < 3 && (
        <p className="text-sm text-foreground-500">Digite mais alguns números do CPF.</p>
      )}
    </div>
  );
}